import { Alert, Button, Input, Modal, Radio, Spin, Upload, message } from "antd";
import type { CustomerReceivingAddress, File as EntityFile, OrderVo, ReceivePayment } from "@zhihaoscm/entity";
import { IS_INCLUDE_TAX, TAX_RATE, TRACK_TYPE, TRADE_PRODUCT_PAY_TYPE } from "@zhihaoscm/meta";
import { formatCurrency } from "@zhihaoscm/utils";
import sensors from "sa-sdk-javascript";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { addressService } from "@/api/addressService";
import { orderService } from "@/api/orderService";
import { receivePaymentService } from "@/api/receivePaymentService";
import { fileService } from "@/api/fileService";
import { paymentService } from "@/api/paymentService";
import { useFullAreaName } from "@/runtime/areaData";
import { WX_ALI_PAY_STATE } from "../../../../../common/enum/payment";
import "./order-confirm.css";

const CONFIRM_REMARK_KEY = "console-order-confirm-remark";
const OFFLINE_ACCEPT = [".zip", ".rar", ".7z", ".aip", ".jpg", ".jpeg", ".png", ".webp", ".avif", ".doc", ".docx", ".xls", ".xlsx", ".pdf"].join(",");

function amount(value?: number) {
    return formatCurrency(value);
}

/** 线下付款凭证提交时限与旧弹窗共用同一倒计时格式。 */
function paymentRemainingTime(value?: string | Date) {
    const remaining = value ? new Date(value).getTime() - Date.now() : 0;
    if (remaining <= 0) return "--";
    const days = Math.floor(remaining / 86400000); const hours = Math.floor((remaining % 86400000) / 3600000); const minutes = Math.floor((remaining % 3600000) / 60000); const seconds = Math.floor((remaining % 60000) / 1000);
    return `${days ? `${days}天` : ""}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/** 复用旧 Element Plus Select 图标的原始矢量路径。 */
function LegacySelectIcon() {
    return (
        <svg aria-hidden="true" data-icon-source="element-select" viewBox="0 0 1024 1024">
            <path fill="currentColor" d="M77.248 415.04a64 64 0 0 1 90.496 0l226.304 226.304L846.528 188.8a64 64 0 1 1 90.56 90.496l-543.04 543.04-316.8-316.8a64 64 0 0 1 0-90.496" />
        </svg>
    );
}

/** 地址卡片保留旧版地区名称、默认标记和右上角选中图标结构。 */
function ConfirmAddressCard({ address, selected, onSelect }: { address: CustomerReceivingAddress; selected: boolean; onSelect: () => void }) {
    const areaName = useFullAreaName(address.province, address.city, address.district);
    return (
        <button className={`confirm-address-item${selected ? " is-selected" : ""}`} onClick={onSelect} type="button">
            <div className="confirm-address-area">
                {address.isDefault ? <span>默认</span> : null}
                {areaName}
            </div>
            <strong title={address.detailAddress}>{address.detailAddress || "--"}</strong>
            <p><span>{address.contacts}</span><span>{address.cellphone}</span></p>
            <i><LegacySelectIcon /></i>
        </button>
    );
}

/** 捡漏订单确认页完成地址选择、订单备注、支付方式和订单确认。 */
export function OrderConfirmPage() {
    const { id = "" } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<OrderVo>();
    const [mainFile, setMainFile] = useState<EntityFile>();
    const [addresses, setAddresses] = useState<CustomerReceivingAddress[]>([]);
    const [addressId, setAddressId] = useState<number>();
    const [remark, setRemark] = useState("");
    const [paymentType, setPaymentType] = useState(1);
    const [showAllAddresses, setShowAllAddresses] = useState(false);
    const [offlineOpen, setOfflineOpen] = useState(false);
    const [offlineFileIds, setOfflineFileIds] = useState<number[]>([]);
    const [paymentInstruction, setPaymentInstruction] = useState("");
    const [offlineSaving, setOfflineSaving] = useState(false);
    const [offlinePayment, setOfflinePayment] = useState<ReceivePayment>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showAddressError, setShowAddressError] = useState(false);
    const [, setClock] = useState(0);
    const paymentTimerRef = useRef<number | undefined>(undefined);
    /** 并行加载订单和地址，优先选取路由地址或默认地址。 */
    const load = async () => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const [order, list] = await Promise.all([orderService.getInfo(id), addressService.getCustomerAddress()]);
            setDetail(order);
            setAddresses(list);
            const queryAddress = Number(searchParams.get("addressId") || 0);
            // Vue 只回显地址新增页带回的 addressId，不自动选择默认地址；带回地址时恢复跳转前备注。
            setAddressId(queryAddress || undefined);
            if (queryAddress) {
                setRemark(sessionStorage.getItem(`${CONFIRM_REMARK_KEY}:${id}`) || "");
                sessionStorage.removeItem(`${CONFIRM_REMARK_KEY}:${id}`);
            }
            setPaymentType([1, 3].includes((order.order.productInfo as any)?.payType) ? 1 : 2);
            const imageId = order.order.productInfo?.imgMainId;
            if (imageId) void fileService.getFileById(imageId).then(setMainFile).catch(() => setMainFile(undefined));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id, searchParams.get("addressId")]);
    useEffect(() => () => window.clearInterval(paymentTimerRef.current), []);
    useEffect(() => { const timer = window.setInterval(() => setClock(value => value + 1), 1000); return () => window.clearInterval(timer); }, []);
    /** 线上支付沿用 Vue 每秒查询支付状态的后续流程。 */
    const pollPaymentState = (outTradeNo?: string) => {
        if (!outTradeNo) return;
        window.clearInterval(paymentTimerRef.current);
        paymentTimerRef.current = window.setInterval(async () => {
            try {
                const state = await paymentService.getState(outTradeNo);
                if (!WX_ALI_PAY_STATE.SUCCESS.match(state)) return;
                window.clearInterval(paymentTimerRef.current);
                void message.success("支付成功");
                navigate(-1);
            } catch {
                window.clearInterval(paymentTimerRef.current);
            }
        }, 1000);
    };
    /** 先确认订单，再根据线上/线下方式进入对应后续流程。 */
    const submit = async () => {
        if (!addressId) {
            setShowAddressError(true);
            Modal.warning({ title: "提示", content: "请选择收货地址" });
            return;
        }
        if (!detail) return;
        setSaving(true);
        try {
            await orderService.confirmOrder(id, {
                orderType: 3,
                productId: detail.order.productId,
                addressId,
                ton: detail.order.ton,
                remark
            });
            if (paymentType === 2) {
                const currentPayment = detail.receivePayments?.[0];
                if (!currentPayment) return;
                const latestPayment = await receivePaymentService.receivePaymentId(currentPayment.id);
                setOfflinePayment(latestPayment);
                setOfflineFileIds(latestPayment.paymentFileId ?? []);
                setPaymentInstruction(latestPayment.paymentInstruction ?? "");
                setOfflineOpen(true);
                return;
            }
            if (detail.receivePayments?.[0]) {
                const payment = await receivePaymentService.paySuccess(detail.receivePayments[0].id);
                const payUrl = (payment as any).payUrl;
                if (payUrl) window.open(payUrl, "_blank");
                pollPaymentState((payment as any).outTradeNo);
            }
            sensors.track("140", { type: TRACK_TYPE.ELEMENT_CLICK.code, productId: detail.order.productId, params: { id, estimated_amount: detail.order.productInfo?.totalCost, prooduct_id: detail.order.productId } });
        } catch {
            void message.error("订单提交失败");
        } finally {
            setSaving(false);
        }
        sensors.track("139", { type: TRACK_TYPE.ELEMENT_CLICK.code, productId: detail.order.productId, params: { id, total_amount: detail.order.productInfo?.totalCost } });
    };
    if (loading) return <Spin fullscreen tip="订单确认信息加载中" />;
    if (error || !detail)
        return (
            <Alert
                type="error"
                showIcon
                message={id ? "订单确认信息加载失败" : "缺少订单编号"}
                action={id ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    const product = detail.order.productInfo;
    const totalCost = Number(product?.totalCost ?? detail.order.estimatedAmount ?? 0);
    const ton = Number(product?.bargainTon ?? detail.order.ton ?? 0);
    const unitPrice = Number(product?.bargainUnitPrice ?? detail.order.unitPrice ?? 0);
    const receivePayment = offlinePayment ?? detail.receivePayments?.[0];
    /** 旧版线下支付弹窗提交付款凭证后返回订单详情。 */
    const submitOfflinePayment = async () => {
        if (!receivePayment) return;
        setOfflineSaving(true);
        try {
            await receivePaymentService.receivePaymentOffLinePay({ id: receivePayment.id, fileIds: offlineFileIds, version: receivePayment.version, paymentInstruction });
            void message.success("操作成功");
            setOfflineOpen(false);
            navigate(`/orderCenter/myOrder/detail/${id}`);
        } catch {
            void message.error("支付凭证提交失败");
        } finally {
            setOfflineSaving(false);
        }
    };
    return (
        <section className="confirm-order">
            <header className="confirm-order-header">订单确认页</header>
            <section className="confirm-order-section confirm-address">
                <div className="confirm-section-title">
                    <strong>
                        确认收货地址
                        {showAddressError ? <small className="confirm-address-error">请选择收货地址</small> : null}
                    </strong>
                    <div>
                        <button
                            onClick={() => { sessionStorage.setItem(`${CONFIRM_REMARK_KEY}:${id}`, remark); navigate(`/me/address/add?fromPage=bargainOrderConfirm&orderId=${id}`); }}
                            type="button"
                        >
                            使用新地址
                        </button>
                        <button onClick={() => navigate("/me/address")} type="button">
                            管理地址
                        </button>
                    </div>
                </div>
                {addresses.length ? (
                    <>
                        <div className={`confirm-address-list${showAllAddresses ? " show-more" : ""}`}>
                            {addresses.map(address => (
                                <ConfirmAddressCard
                                    address={address}
                                    key={address.id}
                                    onSelect={() => { setAddressId(address.id); setShowAddressError(false); }}
                                    selected={addressId === address.id}
                                />
                            ))}
                        </div>
                        {addresses.length > 4 ? (
                            <button
                                className="confirm-address-more"
                                onClick={() => setShowAllAddresses(value => !value)}
                                type="button"
                            >
                                显示全部地址
                                <i className="confirm-address-more-icon">
                                    <svg aria-hidden="true" data-icon-source="element-arrow-down-bold" viewBox="0 0 1024 1024">
                                        <path fill="currentColor" d="M104.704 338.752a64 64 0 0 1 90.496 0l316.8 316.8 316.8-316.8a64 64 0 0 1 90.496 90.496L557.248 791.296a64 64 0 0 1-90.496 0L104.704 429.248a64 64 0 0 1 0-90.496" />
                                    </svg>
                                </i>
                            </button>
                        ) : null}
                    </>
                ) : null}
            </section>
            <section className="confirm-order-section">
                <h2>订单信息</h2>
                <div className="confirm-product">
                    <div className="confirm-product-image">
                        {mainFile?.path ? (
                            <img alt={product?.title || "商品图片"} src={mainFile.path} />
                        ) : null}
                    </div>
                    <div className="confirm-product-middle">
                        <strong>{product?.title || "--"}</strong>
                        <p>
                            <span>品类</span>
                            {product?.productTypeName || "--"}
                        </p>
                        <p>
                            <span>规格</span>
                            {product?.productTypeSpec || "--"}
                        </p>
                        <p>
                            <span>供应商</span>
                                {product?.supplierEnterprise?.name || product?.supplierEnterprise?.realName || "--"}
                        </p>
                    </div>
                    <div className="confirm-product-price">
                        <p>
                            <span>捡漏总价</span>
                            <strong>¥{amount(totalCost)}</strong>
                        </p>
                        <small>{product?.isIncludeTax === IS_INCLUDE_TAX.YES.code ? `含税(${TAX_RATE.getIntroByCode(product.taxRate)})` : "不含税"}</small>
                        <p>
                            <span>数量</span>
                            <span>{amount(ton)} 吨</span>
                        </p>
                    </div>
                </div>
                <div className="confirm-order-remark">
                    <span>订单备注</span>
                    <Input.TextArea
                        value={remark}
                        onChange={event => setRemark(event.target.value)}
                        maxLength={300}
                        showCount
                        rows={3}
                        placeholder="请输入订单备注"
                    />
                </div>
            </section>
            <section className="confirm-order-section confirm-payment">
                <h2>付款信息</h2>
                <div className="confirm-payment-content">
                    <div>
                        <span>预估总货款金额</span>
                        <strong>￥{amount(totalCost)}</strong>
                    </div>
                    <div>
                        <span>商品单价</span>
                        <strong>￥{amount(unitPrice)}/吨</strong>
                    </div>
                    <div>
                        <span>采购吨数</span>
                        <strong>{amount(ton)}</strong>
                    </div>
                    <div>
                        <span>支付货款</span>
                        <strong className="payment-amount">￥{amount(totalCost)}</strong>
                    </div>
                    <div>
                        <span>支付方式</span>
                        {product?.payType === TRADE_PRODUCT_PAY_TYPE.ONLINE_AND_OFFLINE.code ? (
                            <Radio.Group value={paymentType} onChange={event => setPaymentType(event.target.value)}>
                                <Radio value={1}>线上支付</Radio>
                                <Radio value={2}>线下支付</Radio>
                            </Radio.Group>
                        ) : (
                            <strong>{paymentType === 1 ? "线上支付" : "线下支付"}</strong>
                        )}
                    </div>
                    <Button
                        className="confirm-payment-button"
                        loading={saving}
                        onClick={() => void submit()}
                        type="primary"
                    >
                        前往支付
                    </Button>
                </div>
            </section>
            <Modal cancelText="取消" className="offline-payment-modal" confirmLoading={offlineSaving} onCancel={() => { setOfflineOpen(false); navigate(-1); }} onOk={() => Modal.confirm({ title: "提示", content: "是否确认提交？", okText: "确认", cancelText: "取消", onOk: submitOfflinePayment })} okText="提交" open={offlineOpen} title="线下支付" width={800}>
                <div className="offline-payment-amount"><span>应支付金额</span><div><strong><small>¥</small>{amount(receivePayment?.amount ?? totalCost)}</strong><em>请您在{paymentRemainingTime(receivePayment?.payEndTime)}内提交，否则订单会被取消</em></div></div>
                <h3>线下收款账户</h3>
                <div className="offline-account-list">{((product as any)?.accountInfo ?? []).map((account: { account?: string; name?: string; openingBank?: string }, index: number) => <div className="offline-account" key={`${account.account}-${index}`}><div><strong>{account.account?.replace(/.{4}(?=.)/g, "$& ") || "--"}</strong><button onClick={() => { if (account.account) void navigator.clipboard.writeText(account.account); }} type="button">复制</button></div><p><span>开户名称</span>{account.name || "--"}</p><p><span>开户行</span>{account.openingBank || "--"}</p></div>)}</div>
                <div className="offline-payment-field"><label>上传支付凭证 <small>支持最多上传10个文件，单个文件不超过50M（支持上传压缩包）</small></label><Upload accept={OFFLINE_ACCEPT} beforeUpload={file => { if (file.size <= 50 * 1024 * 1024) return true; void message.error("单个文件不能超过50M"); return Upload.LIST_IGNORE; }} customRequest={async options => { const raw = options.file as File; const formData = new FormData(); formData.append("file", raw); formData.append("fileName", raw.name); try { const file = await fileService.uploadFile(formData); setOfflineFileIds(ids => [...ids, file.id]); options.onSuccess?.(file); } catch (reason) { options.onError?.(reason as Error); } }} maxCount={10} multiple><Button><i className="iconfont icon-upload" />上传文件</Button></Upload></div>
                <div className="offline-payment-field"><label>付款说明</label><Input.TextArea maxLength={200} onChange={event => setPaymentInstruction(event.target.value)} placeholder="请输入付款说明" rows={3} showCount value={paymentInstruction} /></div>
            </Modal>
        </section>
    );
}
